/**
 * FlyCastCurveRenderer
 *
 * SVG rendering of the bezier curve + player trace trail for the fly cast minigame.
 * The trace trail is updated imperatively via ref for 60fps performance (no React state).
 */

import { useRef, useEffect, useCallback } from 'react';
import type { FlyCurve, Point2D } from '@/types';
import { flyCurveToSvgPath } from '@/utils/flyCurve';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import type { FlyCastTraceState } from '@/hooks/useFlyCastTrace';

interface FlyCastCurveRendererProps {
  curve: FlyCurve;
  tracePointsRef: React.MutableRefObject<Point2D[]>;
  traceState: FlyCastTraceState;
  lastSampleScore: number;
  currentAccuracy: number;
  traceProgress: number;
}

/** Map a score (0-1) to a trace color */
function getTraceColor(score: number): string {
  const perfectThreshold = BALANCE_CONFIG.FLY_CAST_PERFECT_TOLERANCE / BALANCE_CONFIG.FLY_CAST_GOOD_TOLERANCE;
  if (score >= perfectThreshold) return '#4ade80'; // green-400
  if (score > 0) return '#fbbf24'; // amber-400
  return '#f87171'; // red-400
}

export function FlyCastCurveRenderer({
  curve,
  tracePointsRef,
  traceState,
  lastSampleScore,
  currentAccuracy,
  traceProgress,
}: FlyCastCurveRendererProps) {
  const tracePathRef = useRef<SVGPathElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const svgPath = flyCurveToSvgPath(curve);

  // Viewbox dimensions: curve circle + padding
  const padding = 20;
  const viewSize = curve.circle.radius * 2 + padding * 2;
  const offsetX = curve.circle.cx - curve.circle.radius - padding;
  const offsetY = curve.circle.cy - curve.circle.radius - padding;

  /**
   * Imperatively update the trace trail path from ref data.
   * Runs on rAF for smooth 60fps updates without React re-renders.
   */
  const updateTracePath = useCallback(() => {
    const el = tracePathRef.current;
    if (!el) return;

    const points = tracePointsRef.current;
    if (points.length < 2) {
      el.setAttribute('d', '');
      return;
    }

    // Build SVG path from trace points
    let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    for (let i = 1; i < points.length; i++) {
      d += ` L ${points[i].x.toFixed(1)} ${points[i].y.toFixed(1)}`;
    }
    el.setAttribute('d', d);
  }, [tracePointsRef]);

  // rAF loop for imperative trace rendering while tracing
  useEffect(() => {
    if (traceState !== 'tracing') return;

    const loop = () => {
      updateTracePath();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [traceState, updateTracePath]);

  // Final render when completed
  useEffect(() => {
    if (traceState === 'completed') {
      updateTracePath();
    }
  }, [traceState, updateTracePath]);

  // Clear trace path when idle (new cast)
  useEffect(() => {
    if (traceState === 'idle' && tracePathRef.current) {
      tracePathRef.current.setAttribute('d', '');
    }
  }, [traceState]);

  const traceColor = getTraceColor(lastSampleScore);

  return (
    <svg
      viewBox={`${offsetX} ${offsetY} ${viewSize} ${viewSize}`}
      className="w-full h-full"
      style={{ touchAction: 'none' }}
    >
      {/* Circle boundary */}
      <circle
        cx={curve.circle.cx}
        cy={curve.circle.cy}
        r={curve.circle.radius}
        fill="none"
        stroke="rgba(148, 163, 184, 0.2)"
        strokeWidth="2"
      />

      {/* The bezier curve to trace */}
      <path
        d={svgPath}
        fill="none"
        stroke="rgba(148, 163, 184, 0.4)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Guide curve — brighter when idle */}
      {traceState === 'idle' && (
        <path
          d={svgPath}
          fill="none"
          stroke="rgba(250, 250, 250, 0.3)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ filter: 'blur(2px)' }}
        />
      )}

      {/* Player trace trail — updated imperatively */}
      <path
        ref={tracePathRef}
        fill="none"
        stroke={traceColor}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ willChange: 'd', filter: `drop-shadow(0 0 3px ${traceColor})` }}
      />

      {/* Start point marker */}
      <circle
        cx={curve.start.x}
        cy={curve.start.y}
        r={traceState === 'idle' ? 10 : 6}
        fill={traceState === 'idle' ? '#4ade80' : 'rgba(74, 222, 128, 0.4)'}
        stroke={traceState === 'idle' ? '#ffffff' : 'none'}
        strokeWidth={traceState === 'idle' ? 2 : 0}
      >
        {traceState === 'idle' && (
          <animate
            attributeName="r"
            values="10;13;10"
            dur="1.5s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Start label */}
      {traceState === 'idle' && (
        <text
          x={curve.start.x}
          y={curve.start.y - 16}
          textAnchor="middle"
          fill="#4ade80"
          fontSize="12"
          fontWeight="bold"
        >
          START
        </text>
      )}

      {/* End point marker */}
      <circle
        cx={curve.end.x}
        cy={curve.end.y}
        r={traceState === 'completed' ? 10 : 8}
        fill={
          traceState === 'completed'
            ? '#4ade80'
            : traceProgress > 0.5
              ? 'rgba(250, 204, 21, 0.6)'
              : 'rgba(250, 204, 21, 0.3)'
        }
        stroke={traceState !== 'tracing' ? '#ffffff' : 'none'}
        strokeWidth={traceState !== 'tracing' ? 1.5 : 0}
      />

      {/* End label */}
      {traceState !== 'completed' && (
        <text
          x={curve.end.x}
          y={curve.end.y - 14}
          textAnchor="middle"
          fill="rgba(250, 204, 21, 0.6)"
          fontSize="11"
          fontWeight="bold"
        >
          END
        </text>
      )}

      {/* Accuracy HUD during tracing */}
      {traceState === 'tracing' && (
        <text
          x={curve.circle.cx}
          y={offsetY + 16}
          textAnchor="middle"
          fill={currentAccuracy >= 0.85 ? '#4ade80' : currentAccuracy >= 0.6 ? '#fbbf24' : '#f87171'}
          fontSize="14"
          fontWeight="bold"
        >
          {Math.round(currentAccuracy * 100)}%
        </text>
      )}
    </svg>
  );
}
